import { type Timesheet_Update } from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';
import * as yup from 'yup';

import { Timesheet } from '~hooks/useTimesheets/models';
import { mergeDateTime, minutesToDecimalHours } from '~utils/dateTime';
import { emptyStringToNull } from '~utils/yupTransforms';

const MINUTES_PER_HOUR = 60;

const isValidDayjs = (value: Dayjs | null | undefined) =>
  !value || (dayjs.isDayjs(value) && value.isValid());

// A time without its date (or the reverse) can't be sent, so each half is required once
// the other one is set.
const dateTimePartSchema = (pairedField: string, invalidMessageKey: string) =>
  yup
    .mixed<Dayjs>()
    .nullable()
    .test('valid', () => t(invalidMessageKey), isValidDayjs)
    .test(
      'paired',
      () => t('dispatch.dispatch_v2.enter_both_date_and_time'),
      function hasPairedValue(value) {
        const pairedValue = this.parent[pairedField];

        return Boolean(value) || !pairedValue;
      },
    );

export const timesheetSchema = yup.object({
  breakMinutes: yup
    .number()
    .transform(emptyStringToNull)
    .nullable()
    .typeError(() => t('form_validation_errors.should_be_number_simple'))
    .integer(() =>
      t('form_validation_errors.should_be_integer', {
        field: t('approvals.timesheets.break_in_minutes'),
      }),
    )
    .min(0, () =>
      t('form_validation_errors.min', {
        field: t('approvals.timesheets.break_in_minutes'),
        min: 0,
      }),
    ),
  endedAtDate: dateTimePartSchema('endedAtTime', 'form_validation_errors.invalid_date'),
  endedAtTime: dateTimePartSchema('endedAtDate', 'form_validation_errors.invalid_time'),
  startedAtDate: dateTimePartSchema(
    'startedAtTime',
    'form_validation_errors.invalid_date',
  ),
  startedAtTime: dateTimePartSchema(
    'startedAtDate',
    'form_validation_errors.invalid_time',
  ),
  timesheetNumber: yup.string().nullable().notRequired(),
  totalHours: yup
    .number()
    .transform(emptyStringToNull)
    .nullable()
    .typeError(() => t('form_validation_errors.should_be_number_simple'))
    .min(0, () =>
      t('form_validation_errors.min', {
        field: t('approvals.timesheets.total_hours'),
        min: 0,
      }),
    ),
});

export type TimesheetFormValues = yup.InferType<typeof timesheetSchema>;

export function timesheetToFormValues(timesheet: Timesheet | null): TimesheetFormValues {
  const startedAt = timesheet?.startedAt ? dayjs.tz(timesheet.startedAt) : null;
  const endedAt = timesheet?.endedAt ? dayjs.tz(timesheet.endedAt) : null;

  return {
    breakMinutes: timesheet?.breakMinutes ?? null,
    endedAtDate: endedAt,
    endedAtTime: endedAt,
    startedAtDate: startedAt,
    startedAtTime: startedAt,
    timesheetNumber: timesheet?.timesheetNumber ?? '',
    totalHours: minutesToDecimalHours(timesheet?.totalMinutes ?? null),
  };
}

type DateTimePart = Dayjs | null | undefined;

const dateTimeToIso = (date: DateTimePart, time: DateTimePart) =>
  date && time ? mergeDateTime(date, time) : null;

// The new timestamp when the user changed either half, or undefined when it is unchanged.
function changedDateTime(
  values: TimesheetFormValues,
  initialValues: TimesheetFormValues,
  dateField: 'endedAtDate' | 'startedAtDate',
  timeField: 'endedAtTime' | 'startedAtTime',
) {
  const value = dateTimeToIso(values[dateField], values[timeField]);
  const initialValue = dateTimeToIso(initialValues[dateField], initialValues[timeField]);

  return value === initialValue ? undefined : value;
}

const toMinutes = (hours: number | null | undefined) =>
  hours === null || hours === undefined ? null : Math.round(hours * MINUTES_PER_HOUR);

// Sends only what the user changed, so an untouched field never overwrites the server's
// value. Net minutes are left out: the API derives them as total minus break.
export function timesheetFormValuesToUpdate(
  values: TimesheetFormValues,
  initialValues: TimesheetFormValues,
): Timesheet_Update {
  const body: Timesheet_Update = {};

  // PATCH semantics: a cleared field is sent as null, not an empty string.
  const timesheetNumber = values.timesheetNumber || null;
  if (timesheetNumber !== (initialValues.timesheetNumber || null)) {
    body.timesheet_number = timesheetNumber;
  }

  const startedAt = changedDateTime(
    values,
    initialValues,
    'startedAtDate',
    'startedAtTime',
  );
  if (startedAt !== undefined) {
    body.started_at = startedAt;
  }

  const endedAt = changedDateTime(values, initialValues, 'endedAtDate', 'endedAtTime');
  if (endedAt !== undefined) {
    body.ended_at = endedAt;
  }

  const totalMinutes = toMinutes(values.totalHours);
  if (totalMinutes !== toMinutes(initialValues.totalHours)) {
    body.total_minutes = totalMinutes;
  }

  const breakMinutes = values.breakMinutes ?? null;
  if (breakMinutes !== (initialValues.breakMinutes ?? null)) {
    body.break_minutes = breakMinutes;
  }

  return body;
}
